import React from 'react';
import { motion } from 'framer-motion';
import {
  Users, BookOpen, Eye, CreditCard, TrendingUp,
  Crown, Clock, Trash2, Shield, ShieldOff, BarChart3,
  Link2, Percent, Euro, Plus, Copy, Check as CheckIcon, X,
  Ban, UserCheck, Gift, CalendarPlus, Download, History, MoreVertical, Mail, Send, Tag
} from 'lucide-react';
import { Button } from '../../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { TabsContent } from '../../ui/tabs';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '../../ui/dropdown-menu';

import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const DiscountsTab = ({ ctx }) => {
  const { discountCodes, newDiscount, setNewDiscount, creatingDiscount, setCreatingDiscount, token, setDiscountCodes, API, toast, axios } = ctx;
  return (
  <TabsContent value="discounts" className="space-y-6" data-testid="discounts-content">
              {/* Create New Code */}
              <Card>
                <CardHeader>
                  <CardTitle className="font-display text-xl flex items-center gap-2">
                    <Plus className="w-5 h-5 text-gold" />
                    Nieuwe Kortingscode
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label className="text-xs">Code</Label>
                      <Input
                        data-testid="discount-code-input"
                        placeholder="Bijv. KERST2026"
                        value={newDiscount.code}
                        onChange={(e) => setNewDiscount({...newDiscount, code: e.target.value.toUpperCase()})}
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Type korting</Label>
                      <select
                        data-testid="discount-type-select"
                        className="w-full border border-border rounded-md p-2 bg-background text-sm"
                        value={newDiscount.discount_type}
                        onChange={(e) => setNewDiscount({...newDiscount, discount_type: e.target.value})}
                      >
                        <option value="percentage">Percentage (%)</option>
                        <option value="fixed">Vast bedrag (€)</option>
                      </select>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Waarde ({newDiscount.discount_type === 'percentage' ? '%' : '€'})</Label>
                      <Input
                        data-testid="discount-value-input"
                        type="number"
                        placeholder={newDiscount.discount_type === 'percentage' ? '50' : '1.50'}
                        value={newDiscount.discount_value}
                        onChange={(e) => setNewDiscount({...newDiscount, discount_value: e.target.value})}
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Max gebruik (leeg = onbeperkt)</Label>
                      <Input
                        data-testid="discount-max-uses"
                        type="number"
                        placeholder="100"
                        value={newDiscount.max_uses}
                        onChange={(e) => setNewDiscount({...newDiscount, max_uses: e.target.value})}
                      />
                    </div>
                  </div>
                  <Button
                    data-testid="create-discount-btn"
                    disabled={creatingDiscount || !newDiscount.code || !newDiscount.discount_value}
                    onClick={async () => {
                      setCreatingDiscount(true);
                      try {
                        await axios.post(`${API}/admin/discount-codes`, {
                          code: newDiscount.code,
                          discount_type: newDiscount.discount_type,
                          discount_value: parseFloat(newDiscount.discount_value),
                          max_uses: newDiscount.max_uses ? parseInt(newDiscount.max_uses) : null,
                          expires_at: newDiscount.expires_at || null
                        }, { headers: { Authorization: `Bearer ${token}` } });
                        toast.success('Kortingscode aangemaakt!');
                        setNewDiscount({ code: '', discount_type: 'percentage', discount_value: '', max_uses: '', expires_at: '' });
                        fetchData();
                      } catch (e) {
                        toast.error(e.response?.data?.detail || 'Aanmaken mislukt');
                      } finally {
                        setCreatingDiscount(false);
                      }
                    }}
                    className="w-full sm:w-auto rounded-full"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Aanmaken
                  </Button>
                </CardContent>
              </Card>

              {/* Existing Codes */}
              <Card>
                <CardHeader>
                  <CardTitle className="font-display text-xl flex items-center gap-2">
                    <Tag className="w-5 h-5 text-gold" />
                    Bestaande Codes ({discountCodes.length})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {discountCodes.length > 0 ? (
                    <div className="space-y-3">
                      {discountCodes.map((dc) => (
                        <div key={dc.id} className={`flex flex-col sm:flex-row sm:items-center justify-between p-3 rounded-xl border ${dc.is_active ? 'bg-muted/20 border-border' : 'bg-red-50/50 dark:bg-red-900/10 border-red-200/50'}`}>
                          <div className="flex items-center gap-3 mb-2 sm:mb-0">
                            <span className="font-mono font-bold text-sm bg-gold/10 px-3 py-1 rounded-lg text-gold">{dc.code}</span>
                            <span className="text-sm">
                              {dc.discount_type === 'percentage' ? `${dc.discount_value}%` : `€${dc.discount_value?.toFixed(2)}`} korting
                            </span>
                            <span className="text-xs text-muted-foreground">
                              {dc.used_count || 0}{dc.max_uses ? `/${dc.max_uses}` : ''} gebruikt
                            </span>
                            {!dc.is_active && <span className="text-xs text-red-500 font-medium">Inactief</span>}
                          </div>
                          <div className="flex gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={async () => {
                                await navigator.clipboard.writeText(dc.code);
                                toast.success('Code gekopieerd!');
                              }}
                            >
                              <Copy className="w-3 h-3" />
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={async () => {
                                try {
                                  await axios.post(`${API}/admin/discount-codes/${dc.id}/toggle`, {}, { headers: { Authorization: `Bearer ${token}` }});
                                  fetchData();
                                } catch (e) { toast.error('Fout'); }
                              }}
                            >
                              {dc.is_active ? 'Deactiveer' : 'Activeer'}
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              className="text-destructive"
                              onClick={async () => {
                                try {
                                  await axios.delete(`${API}/admin/discount-codes/${dc.id}`, { headers: { Authorization: `Bearer ${token}` }});
                                  toast.success('Verwijderd');
                                  fetchData();
                                } catch (e) { toast.error('Fout'); }
                              }}
                            >
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-muted-foreground">
                      <Tag className="w-10 h-10 mx-auto mb-2 opacity-30" />
                      <p>Nog geen kortingscodes aangemaakt</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
  );
};
